import { useState, useEffect, useRef } from "react";
import {
  FaShareAlt,
  FaLink,
  FaFacebookF,
  FaInstagram,
  FaEnvelope,
  FaSms,
  FaWhatsapp,
  FaCheck,
} from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6";

interface ShareButtonProps {
  shareText: string;
}

const ShareButton = ({ shareText }: ShareButtonProps) => {
  const [showOptions, setShowOptions] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentUrl = typeof window !== "undefined" ? window.location.href : "";

  const handleClickOutside = (event: MouseEvent) => {
    if (
      containerRef.current &&
      !containerRef.current.contains(event.target as Node)
    ) {
      setShowOptions(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleShare = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (navigator.share) {
      try {
        await navigator.share({
          title: document.title,
          text: shareText,
          url: currentUrl,
        });
        return;
      } catch (err) {
        console.error("Native share failed:", err);
      }
    }
    setShowOptions((prev) => !prev);
  };

  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(currentUrl);
      setIsCopied(true);
      setShowOptions(false);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  const isMobile = /Android|iPhone/i.test(navigator.userAgent);
  const instagramHref = isMobile
    ? `instagram://share?text=${encodeURIComponent(`${shareText}: ${currentUrl}`)}`
    : "https://www.instagram.com/";

  const shareOptions = [
    { name: "Copy Link", icon: <FaLink />, onClick: handleCopy, href: "#" },
    {
      name: "WhatsApp",
      icon: <FaWhatsapp />,
      href: `https://api.whatsapp.com/send?text=${encodeURIComponent(`${shareText}:  ${currentUrl}`)}`,
    },
    ...(isMobile
      ? [
          {
            name: "Messenger",
            icon: <FaFacebookF />,
            href: `https://www.facebook.com/dialog/send?app_id=123456789&link=${encodeURIComponent(currentUrl)}&redirect_uri=${encodeURIComponent(currentUrl)}`,
          },
          {
            name: "Instagram",
            icon: <FaInstagram />,
            href: "instagram://app", // opens app
          },
        ]
      : []),
    {
      name: "Email",
      icon: <FaEnvelope />,
      href: `mailto:?subject=${encodeURIComponent(shareText)}&body=${encodeURIComponent(currentUrl)}`,
    },
    {
      name: "Message",
      icon: <FaSms />,
      href: `sms:?body=${encodeURIComponent(`${shareText}: ${currentUrl}`)}`,
    },
    {
      name: "X (Twitter)",
      icon: <FaXTwitter />,
      href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(shareText)}`,
    },
  ];

  return (
    <div className="d-inline-block" ref={containerRef}>
      <button
        className="btn btn-primary d-flex align-items-center gap-2 py-2 px-3 rounded-pill"
        style={{ backgroundColor: "#3F41D1" }}
        onClick={handleShare}
      >
        {isCopied ? <FaCheck /> : <FaShareAlt />}
        {isCopied ? "Copied" : "Share"}
      </button>

      <div className="position-relative">
        {showOptions && (
          <div
            className="position-absolute top-100 start-50 translate-middle-x z-3 bg-white border rounded-3 shadow p-1 w-100 w-sm-auto"
            style={{ minWidth: "150px" }}
          >
            {shareOptions.map((opt, i) => (
              <a
                key={i}
                href={opt.href}
                target={opt.href === "#" ? undefined : "_blank"}
                rel={opt.href === "#" ? undefined : "noopener noreferrer"}
                onClick={opt.onClick}
                className="d-flex align-items-center gap-2 py-2 text-dark text-decoration-none rounded-pill hover-bg-light"
              >
                <div
                  className="bg-light d-flex align-items-center justify-content-center rounded-circle"
                  style={{ width: 32, height: 22 }}
                >
                  {opt.icon}
                </div>
                <span>{opt.name}</span>
              </a>
            ))}
          </div>
        )}
      </div>

      {isCopied && (
        <div className="position-fixed top-0 end-0 m-3 p-2 bg-success text-white rounded">
          Link copied to clipboard!
        </div>
      )}
    </div>
  );
};

export default ShareButton;
